import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, ChevronLeft, Activity, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { AccentUsageReport } from "@/components/AccentUsageReport";

const BAND = {
  normal: { t: "Normal", c: "bg-mint" },
  elevated: { t: "Elevated", c: "bg-butter" },
  unusually_high: { t: "Unusually high", c: "bg-coral text-white" },
};
const FEATURE_LABEL = { mission: "Missions", conversation: "Conversations", speech_to_text: "Speech-to-text", text_to_speech: "Text-to-speech", text_lens: "Text Lens", object_lens: "Object Lens", study_lens: "Study Lens", scene_lens: "Scene Lens" };

export default function AiUsage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ features: [], models: [], organizations: [] });
  const [f, setF] = useState({ from: "", to: "", organization_id: "", feature: "", model: "" });
  const [err, setErr] = useState(false);

  const canUse = user?.is_owner === true;

  const load = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    api.get(`/admin/usage/summary?${p.toString()}`).then((r) => setData(r.data)).catch(() => setErr(true));
  };
  useEffect(() => { if (canUse) { api.get("/admin/usage/filters").then((r) => setFilters(r.data)).catch(() => {}); } }, [canUse]);
  useEffect(() => { if (canUse) load(); /* eslint-disable-next-line */ }, [canUse, f]);

  if (!canUse) return <AppLayout><div className="max-w-2xl mx-auto px-4 py-12"><p className="text-coral">This area is restricted to the account owner.</p></div></AppLayout>;

  return (
    <AppLayout>
      <Seo title="AI Usage · FluentLoop" noindex />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/settings" className="text-sm text-gray-600 flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Settings</Link>
        <h1 className="font-cabinet font-extrabold text-3xl flex items-center gap-2 mt-2"><BarChart3 className="w-7 h-7" /> AI Usage</h1>
        <p className="text-gray-600 text-sm mt-1">Owner-only economic observability. Counts &amp; latency only — no prompts, conversations or costs are stored.</p>

        {err && <p className="text-coral mt-4">Could not load usage.</p>}

        {/* Filters */}
        <div className="nb-card p-3 mt-5 flex flex-wrap items-end gap-3" data-testid="usage-filters">
          <div><label className="text-[11px] font-bold uppercase text-gray-500">From</label><input type="date" className="nb-input py-1.5 block" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value ? e.target.value + "T00:00:00+00:00" : "" })} data-testid="usage-from" /></div>
          <div><label className="text-[11px] font-bold uppercase text-gray-500">To</label><input type="date" className="nb-input py-1.5 block" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value ? e.target.value + "T23:59:59+00:00" : "" })} /></div>
          <div><label className="text-[11px] font-bold uppercase text-gray-500">School</label><select className="nb-input py-1.5 block" value={f.organization_id} onChange={(e) => setF({ ...f, organization_id: e.target.value })} data-testid="usage-org"><option value="">All</option>{filters.organizations.map((o) => <option key={o.organization_id} value={o.organization_id}>{o.name}</option>)}</select></div>
          <div><label className="text-[11px] font-bold uppercase text-gray-500">Feature</label><select className="nb-input py-1.5 block" value={f.feature} onChange={(e) => setF({ ...f, feature: e.target.value })}><option value="">All</option>{filters.features.map((x) => <option key={x} value={x}>{FEATURE_LABEL[x] || x}</option>)}</select></div>
          <div><label className="text-[11px] font-bold uppercase text-gray-500">Model</label><select className="nb-input py-1.5 block" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })}><option value="">All</option>{filters.models.map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
          {(f.from || f.to || f.organization_id || f.feature || f.model) && <button onClick={() => setF({ from: "", to: "", organization_id: "", feature: "", model: "" })} className="nb-btn-outline py-1.5 px-3 text-xs">Reset</button>}
        </div>

        {!data ? <div className="nb-card h-32 animate-pulse mt-5" /> : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5" data-testid="usage-totals">
              <Stat icon={Activity} label="Today" v={data.totals.today} />
              <Stat icon={Activity} label="This week" v={data.totals.week} />
              <Stat icon={Activity} label="This month" v={data.totals.month} />
              <Stat icon={CheckCircle2} label="Success" v={data.totals.success} tone="bg-mint" />
              <Stat icon={XCircle} label="Failed" v={data.totals.failed} tone={data.totals.failed ? "bg-coral text-white" : "bg-white"} />
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <Breakdown title="By feature" rows={data.by_feature.map((x) => ({ k: FEATURE_LABEL[x.key] || x.key, c: x.count }))} />
              <Breakdown title="By model" rows={data.by_model.map((x) => ({ k: x.key, c: x.count }))} />
            </div>

            {data.conversation_modes && (data.conversation_modes.voice + data.conversation_modes.text) > 0 && (
              <div className="nb-card p-4 mt-4" data-testid="usage-voice-text">
                <div className="text-xs font-bold uppercase text-gray-500 mb-2">Conversation: voice vs text</div>
                <div className="flex h-3 rounded-full overflow-hidden border border-ink">
                  <div className="bg-coral" style={{ width: `${data.conversation_modes.voice_pct || 0}%` }} title="Voice" />
                  <div className="bg-sky" style={{ width: `${data.conversation_modes.text_pct || 0}%` }} title="Text" />
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-coral rounded-sm inline-block" /> Voice <b>{data.conversation_modes.voice}</b> ({data.conversation_modes.voice_pct ?? 0}%)</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-sky rounded-sm inline-block" /> Text <b>{data.conversation_modes.text}</b> ({data.conversation_modes.text_pct ?? 0}%)</span>
                </div>
              </div>
            )}

            <h3 className="font-cabinet font-bold text-xl mt-8 mb-2">By school</h3>
            <div className="space-y-2" data-testid="usage-schools">
              {data.by_school.length === 0 && <p className="text-sm text-gray-500">No school-attributed AI usage in range.</p>}
              {data.by_school.map((s) => (
                <div key={s.organization_id} className="nb-card p-3 flex items-center justify-between gap-3">
                  <div><div className="font-bold">{s.name}</div><div className="text-xs text-gray-500">{s.mission_generations} missions · {s.conversations} conversations{(s.voice_conversations + s.text_conversations) > 0 ? ` (${s.voice_pct ?? 0}% voice)` : ""} · {s.active_ai_teachers} teachers using AI</div></div>
                  <div className="flex items-center gap-2"><span className="font-cabinet font-extrabold text-lg">{s.total}</span><Anomaly a={s.anomaly} /></div>
                </div>
              ))}
            </div>

            <h3 className="font-cabinet font-bold text-xl mt-8 mb-2">By teacher</h3>
            <div className="space-y-2" data-testid="usage-teachers">
              {data.by_teacher.length === 0 && <p className="text-sm text-gray-500">No usage in range.</p>}
              {data.by_teacher.map((t) => (
                <div key={t.user_id} className="nb-card p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0"><div className="font-bold truncate">{t.name}</div><div className="text-xs text-gray-500 truncate">{t.school || "—"} · {t.mission_generations} missions · {t.conversations} convos{t.last_activity ? ` · last ${new Date(t.last_activity).toLocaleDateString()}` : ""}</div></div>
                  <div className="flex items-center gap-2"><span className="font-cabinet font-extrabold text-lg">{t.total}</span><Anomaly a={t.anomaly} /></div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-6">{data.note}</p>
            <AccentUsageReport />
          </>
        )}
      </div>
    </AppLayout>
  );
}

function Stat({ icon: Icon, label, v, tone = "bg-white" }) {
  return <div className={`nb-card p-4 ${tone}`}><Icon className="w-4 h-4 opacity-70" /><div className="font-cabinet font-extrabold text-2xl mt-1">{v}</div><div className="text-xs opacity-70">{label}</div></div>;
}
function Breakdown({ title, rows }) {
  const max = Math.max(1, ...rows.map((r) => r.c));
  return (
    <div className="nb-card p-4">
      <div className="text-xs font-bold uppercase text-gray-500 mb-2">{title}</div>
      {rows.length === 0 && <p className="text-sm text-gray-400">No data</p>}
      {rows.map((r) => (
        <div key={r.k} className="mb-2"><div className="flex justify-between text-sm"><span>{r.k}</span><b>{r.c}</b></div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-coral" style={{ width: `${(r.c / max) * 100}%` }} /></div></div>
      ))}
    </div>
  );
}
function Anomaly({ a }) {
  const b = BAND[a] || BAND.normal;
  return <span className={`nb-tag text-[10px] py-0 ${b.c} flex items-center gap-1`}>{a === "unusually_high" && <AlertTriangle className="w-3 h-3" />}{b.t}</span>;
}
